import { Button } from "@/components/ui/button"
import type { AnimationEvent } from "@/types"

import { getActionColor } from "./action-colors"
import { useTurnGroups } from "./use-turn-groups"

interface FishEyeSeekBarProps {
  events: AnimationEvent[]
  currentEventIndex: number
  homeTeamId: string
  awayTeamId: string
  homeTeamHue?: number
  awayTeamHue?: number
  onSeek: (index: number) => void
}

/** Team pip color: derived from team hues, neutral=muted gold. */
function teamPipColor(
  teamId: string | null,
  homeTeamId: string,
  awayTeamId: string,
  homeHue: number,
  awayHue: number,
): string {
  if (teamId === homeTeamId) return `oklch(0.50 0.15 ${homeHue})`
  if (teamId === awayTeamId) return `oklch(0.48 0.15 ${awayHue})`
  return "oklch(0.55 0.10 85)" // neutral gold
}

/**
 * FishEyeSeekBar
 *
 * Replaces the basic range-input seek bar with a spatial, turn-aware timeline:
 *
 * [past markers] [H2 divider?] [expanded current turn] [future markers]
 *
 * - Past turns: 8×8px filled pips (team-colored), clickable, seek to turn start
 * - Future turns: 8×8px hollow pips (50% opacity), clickable, seek to turn start
 * - Half divider: 1px vertical gold line with "H2" label between halves
 * - Current turn: expanded flex-1 section showing per-event action segments
 *   - Each segment: 10px tall bar (action color if played, gold if current, dim hollow if future)
 *   - Player abbr label below each segment (from events[i].actingPlayerId via players map — not
 *     available directly, so we show a dot as placeholder)
 *
 * Also renders an event counter (N / Total) for E2E test compatibility.
 */
export function FishEyeSeekBar({
  events,
  currentEventIndex,
  homeTeamId,
  awayTeamId,
  homeTeamHue = 145,
  awayTeamHue = 275,
  onSeek,
}: FishEyeSeekBarProps) {
  const { turnGroups, currentGroupIndex } = useTurnGroups(
    events,
    currentEventIndex,
  )

  if (events.length === 0) {
    return (
      <div
        className="flex flex-1 items-center gap-2"
        data-testid="fish-eye-seek-bar"
      />
    )
  }

  // Insert half-boundary markers: find if there's a group switch from H1 → H2
  // We track this by checking adjacent groups' half values.

  return (
    <div
      className="flex flex-1 items-center gap-1 overflow-hidden"
      data-testid="fish-eye-seek-bar"
      style={{ minWidth: 0 }}
    >
      {/* Past turn markers */}
      {turnGroups.slice(0, currentGroupIndex).map((group, gi) => {
        const color = teamPipColor(
          group.teamId,
          homeTeamId,
          awayTeamId,
          homeTeamHue,
          awayTeamHue,
        )
        // Insert half divider before the first H2 group
        const prevHalf = gi > 0 ? turnGroups[gi - 1].half : 1
        const showHalfDivider = group.half === 2 && prevHalf === 1

        return (
          <div key={gi} className="flex items-center gap-1">
            {showHalfDivider && <HalfDivider label="H2" />}
            <Button
              variant="ghost"
              onClick={() => onSeek(group.startIndex)}
              data-testid="turn-marker"
              data-future="false"
              data-group-index={gi}
              title={`${group.turnLabel} — click to seek`}
              className="h-auto w-auto min-w-0 rounded-none p-0"
              style={{
                width: "8px",
                height: "8px",
                background: color,
                border: `1px solid ${color}`,
                cursor: "pointer",
                flexShrink: 0,
              }}
              aria-label={`Seek to ${group.turnLabel}`}
            />
          </div>
        )
      })}

      {/* Expanded current turn */}
      {turnGroups.length > 0 && (
        <CurrentTurnExpansion
          group={turnGroups[currentGroupIndex]}
          prevGroup={
            currentGroupIndex > 0 ? turnGroups[currentGroupIndex - 1] : null
          }
          events={events}
          currentEventIndex={currentEventIndex}
          homeTeamId={homeTeamId}
          awayTeamId={awayTeamId}
          homeTeamHue={homeTeamHue}
          awayTeamHue={awayTeamHue}
          onSeek={onSeek}
        />
      )}

      {/* Future turn markers */}
      {turnGroups.slice(currentGroupIndex + 1).map((group, sliceGi) => {
        const gi = currentGroupIndex + 1 + sliceGi
        const prevGroup = gi > 0 ? turnGroups[gi - 1] : null
        const showHalfDivider =
          group.half === 2 && prevGroup !== null && prevGroup.half === 1
        const color = teamPipColor(
          group.teamId,
          homeTeamId,
          awayTeamId,
          homeTeamHue,
          awayTeamHue,
        )

        return (
          <div key={gi} className="flex items-center gap-1">
            {showHalfDivider && <HalfDivider label="H2" />}
            <Button
              variant="ghost"
              onClick={() => onSeek(group.startIndex)}
              data-testid="turn-marker"
              data-future="true"
              data-group-index={gi}
              title={`${group.turnLabel} — click to seek`}
              className="h-auto w-auto min-w-0 rounded-none p-0"
              style={{
                width: "8px",
                height: "8px",
                background: "transparent",
                border: `1.5px solid ${color}`,
                opacity: 0.5,
                cursor: "pointer",
                flexShrink: 0,
              }}
              aria-label={`Seek to ${group.turnLabel}`}
            />
          </div>
        )
      })}

      {/* Event counter — kept for E2E test compatibility */}
      <span
        className="shrink-0 text-xs tabular-nums"
        style={{
          color: "oklch(0.55 0.01 230)",
          minWidth: "52px",
          textAlign: "right",
          marginLeft: "4px",
        }}
        data-testid="event-counter"
      >
        {currentEventIndex + 1} / {events.length}
      </span>
    </div>
  )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function HalfDivider({ label }: { label: string }) {
  return (
    <div
      className="flex flex-col items-center"
      style={{ flexShrink: 0 }}
      aria-label={label}
    >
      <span
        style={{
          fontSize: "7px",
          color: "oklch(0.60 0.12 85)",
          lineHeight: 1,
          fontWeight: 700,
        }}
      >
        {label}
      </span>
      <div
        style={{
          width: "1px",
          height: "16px",
          background: "oklch(0.60 0.12 85)",
        }}
      />
    </div>
  )
}

interface CurrentTurnExpansionProps {
  group: ReturnType<typeof useTurnGroups>["turnGroups"][number]
  prevGroup: ReturnType<typeof useTurnGroups>["turnGroups"][number] | null
  events: AnimationEvent[]
  currentEventIndex: number
  homeTeamId: string
  awayTeamId: string
  homeTeamHue: number
  awayTeamHue: number
  onSeek: (index: number) => void
}

function CurrentTurnExpansion({
  group,
  prevGroup,
  events,
  currentEventIndex,
  homeTeamId,
  awayTeamId,
  homeTeamHue,
  awayTeamHue,
  onSeek,
}: CurrentTurnExpansionProps) {
  const showHalfDivider =
    group.half === 2 && prevGroup !== null && prevGroup.half === 1
  const teamColor = teamPipColor(
    group.teamId,
    homeTeamId,
    awayTeamId,
    homeTeamHue,
    awayTeamHue,
  )

  // Events within this group
  const groupEvents = events.slice(group.startIndex, group.endIndex + 1)

  return (
    <div
      className="flex min-w-0 flex-1 flex-col"
      style={{
        border: "1px solid oklch(0.25 0.01 230 / 0.6)",
        padding: "3px 5px",
        background: "oklch(0.14 0.005 230)",
        position: "relative",
      }}
      data-testid="current-turn-expansion"
    >
      {showHalfDivider && (
        <div
          style={{
            position: "absolute",
            left: "-12px",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <HalfDivider label="H2" />
        </div>
      )}

      {/* Turn header */}
      <div
        style={{
          fontSize: "9px",
          fontWeight: 700,
          color: teamColor,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          lineHeight: 1,
          marginBottom: "3px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontFamily: "var(--font-family-display)",
        }}
      >
        {group.turnLabel}
      </div>

      {/* Per-event action segments */}
      <div
        className="flex min-w-0 gap-[1px]"
        style={{ alignItems: "flex-end" }}
      >
        {groupEvents.map((event, relIdx) => {
          const absIdx = group.startIndex + relIdx
          const isPast = absIdx < currentEventIndex
          const isCurrent = absIdx === currentEventIndex
          const isFuture = absIdx > currentEventIndex

          const actionColor = getActionColor(event)

          let segBg: string
          let segHeight: string
          let segBorder: string

          if (isCurrent) {
            segBg = actionColor ?? "oklch(0.60 0.12 85)"
            segHeight = "14px"
            segBorder = `1px solid ${actionColor ?? "oklch(0.65 0.14 85)"}`
          } else if (isPast) {
            segBg = actionColor
              ? actionColor.replace(")", " / 0.5)")
              : "oklch(0.25 0.01 230)"
            segHeight = "10px"
            segBorder = "1px solid transparent"
          } else {
            // Future
            segBg = "transparent"
            segHeight = "10px"
            segBorder = "1px solid oklch(0.20 0.008 230)"
          }

          return (
            <Button
              key={absIdx}
              variant="ghost"
              onClick={() => onSeek(absIdx)}
              data-testid="event-segment"
              data-event-index={absIdx}
              data-current={isCurrent ? "true" : undefined}
              className="h-auto min-w-1 rounded-none p-0"
              style={{
                flex: 1,
                height: segHeight,
                background: segBg,
                border: segBorder,
                cursor: "pointer",
                transition: "height 0.1s",
              }}
              aria-label={`Seek to event ${absIdx + 1}${isFuture ? " (future)" : ""}`}
              title={`Event ${absIdx + 1}`}
            />
          )
        })}
      </div>
    </div>
  )
}
